import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_InlineBlock = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📦 CSS Inline-Block</h1>

      <p className="tutorial-intro">
        The <code>inline-block</code> value of the <code>display</code> property
        allows an element to behave like an inline element (sits next to others)
        while also behaving like a block element (you can set width, height,
        margin, and padding).
      </p>

      {/* Why Use Inline-Block */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#0369a1", marginBottom: "0.5rem" }}>
          🌟 Why Use Inline-Block?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          <code>inline-block</code> is useful when you want elements to appear
          side by side, but still keep the ability to control their size and
          spacing, unlike pure <code>inline</code>.
        </p>
      </div>

      {/* Example 1: Basic Inline-Block */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Inline-Block Example</h2>
        <p>
          Elements with <code>display: inline-block;</code> sit next to each
          other like inline elements, but allow width and height adjustments.
        </p>

        <CodeCopyBlock
          code={`.box {
  display: inline-block;
  width: 120px;
  height: 80px;
  margin: 5px;
  background: #60a5fa;
  color: white;
  text-align: center;
  line-height: 80px;
  border-radius: 6px;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <div
            style={{
              display: "inline-block",
              width: "120px",
              height: "80px",
              margin: "5px",
              background: "#60a5fa",
              color: "white",
              textAlign: "center",
              lineHeight: "80px",
              borderRadius: "6px",
            }}
          >
            Box 1
          </div>
          <div
            style={{
              display: "inline-block",
              width: "120px",
              height: "80px",
              margin: "5px",
              background: "#34d399",
              color: "white",
              textAlign: "center",
              lineHeight: "80px",
              borderRadius: "6px",
            }}
          >
            Box 2
          </div>
          <div
            style={{
              display: "inline-block",
              width: "120px",
              height: "80px",
              margin: "5px",
              background: "#f87171",
              color: "white",
              textAlign: "center",
              lineHeight: "80px",
              borderRadius: "6px",
            }}
          >
            Box 3
          </div>
        </div>
      </div>

      {/* Example 2: Inline vs Inline-Block */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Inline vs Inline-Block</h2>
        <p>
          Inline elements ignore width/height, but inline-block respects them.
          Notice how the inline elements do not expand.
        </p>

        <CodeCopyBlock
          code={`.inline {
  display: inline;
  width: 120px; /* ignored */
  height: 60px; /* ignored */
  background: #fbbf24;
}

.inline-block {
  display: inline-block;
  width: 120px;
  height: 60px;
  background: #a78bfa;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <span
            style={{
              display: "inline",
              width: "120px",
              height: "60px",
              background: "#fbbf24",
              marginRight: "10px",
              padding: "5px",
              borderRadius: "4px",
            }}
          >
            Inline
          </span>
          <span
            style={{
              display: "inline-block",
              width: "120px",
              height: "60px",
              background: "#a78bfa",
              marginRight: "10px",
              padding: "5px",
              textAlign: "center",
              lineHeight: "50px",
              borderRadius: "4px",
              color: "white",
            }}
          >
            Inline-Block
          </span>
        </div>
      </div>

      {/* Example 3: Navigation Menu */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Inline-Block for Navigation</h2>
        <p>
          <code>inline-block</code> is often used for horizontal navigation
          menus.
        </p>

        <CodeCopyBlock
          code={`nav a {
  display: inline-block;
  padding: 10px 20px;
  margin: 5px;
  background: #38bdf8;
  color: white;
  text-decoration: none;
  border-radius: 6px;
}

nav a:hover {
  background: #0ea5e9;
}`}
        />

        <nav style={{ marginTop: "1rem" }}>
          <a
            href="#home"
            style={{
              display: "inline-block",
              padding: "10px 20px",
              margin: "5px",
              background: "#38bdf8",
              color: "white",
              textDecoration: "none",
              borderRadius: "6px",
            }}
          >
            Home
          </a>
          <a
            href="#about"
            style={{
              display: "inline-block",
              padding: "10px 20px",
              margin: "5px",
              background: "#38bdf8",
              color: "white",
              textDecoration: "none",
              borderRadius: "6px",
            }}
          >
            About
          </a>
          <a
            href="#contact"
            style={{
              display: "inline-block",
              padding: "10px 20px",
              margin: "5px",
              background: "#38bdf8",
              color: "white",
              textDecoration: "none",
              borderRadius: "6px",
            }}
          >
            Contact
          </a>
        </nav>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you understand <code>inline-block</code>, try exploring{" "}
          <a href="/css/flexbox" className="learn-more">
            Flexbox
          </a>{" "}
          and{" "}
          <a href="/css/grid" className="learn-more">
            CSS Grid
          </a>{" "}
          which are modern techniques for building layouts without float or
          inline-block hacks.
        </p>
      </div>
    </div>
  );
};

export default CSS_InlineBlock;
